export function getInactiveRenderStatus(asset) {
    if (asset?.geometryStatus === 'failed') {
        return 'failed';
    }

    if (asset?.geometryStatus === 'ready' && asset?.textureStatus !== 'loading') {
        return 'prefetched';
    }

    if (asset?.geometryStatus === 'loading' || asset?.textureStatus === 'loading') {
        return 'prefetching';
    }

    return 'idle';
}

export function getRuntimeRenderStatus({
    asset,
    currentRenderStatus,
    isRenderableActive,
}) {
    if (currentRenderStatus) {
        return currentRenderStatus;
    }

    if (!isRenderableActive) {
        return getInactiveRenderStatus(asset);
    }

    return 'loading';
}

export function buildRuntimeModelState({
    asset,
    capabilities,
    currentRenderStatus,
    isRenderableActive,
    model,
}) {
    const defaultGeometryStatus = isRenderableActive ? 'loading' : 'idle';
    const defaultTextureStatus = model.textureUrl
        ? (isRenderableActive ? 'loading' : 'idle')
        : 'idle';

    return {
        format: model.format,
        geometryStatus: asset?.geometryStatus || defaultGeometryStatus,
        renderStatus: getRuntimeRenderStatus({
            asset,
            currentRenderStatus,
            isRenderableActive,
        }),
        supportsColor: capabilities?.supportsColor ?? (model.format === 'stl' ? true : null),
        supportsTexture: capabilities?.supportsTexture ?? null,
        textureStatus: asset?.textureStatus || defaultTextureStatus,
    };
}

export function buildRuntimeModelStateById({
    assetsById,
    handleCapabilitiesByModelId,
    renderStatuses,
    sceneModels,
    visibleModelIdSet,
}) {
    return Object.fromEntries(sceneModels.map((model) => {
        const asset = assetsById[model.id];
        const capabilities = handleCapabilitiesByModelId[model.id];
        const isRenderableActive = model.shouldBuild && visibleModelIdSet.has(model.id);

        return [
            model.id,
            buildRuntimeModelState({
                asset,
                capabilities,
                currentRenderStatus: renderStatuses[model.id],
                isRenderableActive,
                model,
            }),
        ];
    }));
}

export function isRenderStatusLoading(status) {
    return status === 'loading' || status === 'building';
}

export function getIsSceneLoading(buildableSceneModels, renderStatuses) {
    if (buildableSceneModels.length === 0) {
        return false;
    }

    return buildableSceneModels.some((model) => isRenderStatusLoading(
        renderStatuses[model.id] || 'loading',
    ));
}

export function buildNextRenderStatuses(currentStatuses, sceneModels, visibleModelIdSet) {
    const nextStatuses = {};

    sceneModels.forEach((model) => {
        const currentStatus = currentStatuses[model.id];
        const isRenderableActive = model.shouldBuild && visibleModelIdSet.has(model.id);

        if (!isRenderableActive) {
            nextStatuses[model.id] = currentStatus === 'failed' ? 'failed' : 'idle';
            return;
        }

        if (
            currentStatus === 'ready'
            || currentStatus === 'failed'
            || currentStatus === 'loading'
            || currentStatus === 'building'
        ) {
            nextStatuses[model.id] = currentStatus;
            return;
        }

        nextStatuses[model.id] = 'loading';
    });

    return nextStatuses;
}

export function areRenderStatusMapsEqual(leftStatuses, rightStatuses) {
    const leftKeys = Object.keys(leftStatuses);
    const rightKeys = Object.keys(rightStatuses);

    return leftKeys.length === rightKeys.length
        && rightKeys.every((modelId) => leftStatuses[modelId] === rightStatuses[modelId]);
}

export function areBuildableSceneModelsSettled(buildableSceneModels, renderStatuses) {
    return buildableSceneModels.every((model) => {
        const status = renderStatuses[model.id];

        return status === 'ready' || status === 'failed';
    });
}
